<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { PartnerDef } from '~/utils/diffusion'
import { computed, ref } from 'vue'

// `class` est déclarée en prop (plutôt que laissée en attribut de fallthrough)
// pour être passée à `SearchSidebar`, qui la fusionne via `cn` : c'est ce qui
// permet au drawer mobile de neutraliser le `sticky w-[278px]` de la sidebar.
const props = withDefaults(
  defineProps<{ class?: HTMLAttributes['class'], showTitle?: boolean }>(),
  { showTitle: true },
)

// Émis à la validation du formulaire, pour que le drawer mobile se referme.
const emit = defineEmits<{ submit: [] }>()

const transactionItems = TRANSACTION_FILTER_ITEMS
const partenaireItems = PARTNERS_CATALOG.map(p => ({ label: p.logoAlt, value: p.id, icon: p.icon }))
const supportItems = ['Web', 'Print']
const statutItems = [...STATUT_FILTER_ITEMS]

const reference = ref('')
const localite = ref('')
const { searchTerm: localiteSearchTerm, items: localiteItems } = useLocaliteSearch()
const transaction = ref<string[]>([])
const partenaire = ref<PartnerDef['id'][]>([])
const support = ref<string[]>(['Web'])
const statut = ref<StatutFilterItem[]>([])

const { apply, reset } = useDiffusionFilters()

const partenaireDisplayLabel = computed(() => {
  const count = partenaire.value.length
  if (count === 0)
    return null
  if (count === partenaireItems.length)
    return 'Tous les partenaires'
  if (count === 1)
    return partenaireItems.find(p => p.value === partenaire.value[0])?.label ?? '1 partenaire'
  return `${count} partenaires`
})

function onSubmit() {
  apply({
    reference: reference.value,
    localite: localite.value,
    transaction: transaction.value,
    partenaire: partenaire.value,
    statut: statut.value,
  })
  emit('submit')
}

function onCancel() {
  reference.value = ''
  localite.value = ''
  transaction.value = []
  partenaire.value = []
  support.value = ['Web']
  statut.value = []
  reset()
}
</script>

<template>
  <SearchSidebar
    cancel-label="Annuler"
    :show-title="props.showTitle"
    :class="props.class"
    @submit="onSubmit"
    @cancel="onCancel"
  >
    <UFormField label="Référence">
      <UInput v-model="reference" placeholder="Ref." icon="i-lucide-refresh-ccw" class="w-full" />
    </UFormField>

    <UFormField label="Localité">
      <!-- La valeur sélectionnée reste `label` : le filtre de diffusion la compare à `row.location`. -->
      <UInputMenu
        v-model="localite"
        v-model:search-term="localiteSearchTerm"
        :items="localiteItems"
        value-key="label"
        v-bind="LOCALITE_MENU_PROPS"
        placeholder="Ville, CP..."
        icon="i-lucide-locate-fixed"
        aria-label="Localité"
        class="w-full"
      >
        <template #item-label="{ item }">
          {{ item.label }}
          <span v-if="item.parent" class="text-muted"> → {{ item.parent }}</span>
        </template>
      </UInputMenu>
    </UFormField>

    <UFormField label="Transaction">
      <USelectMenu
        v-model="transaction"
        :items="transactionItems"
        multiple
        :search-input="false"
        placeholder="Tous"
        aria-label="Transaction"
        class="w-full"
      />
    </UFormField>

    <UFormField label="Partenaires">
      <USelectMenu
        v-model="partenaire"
        :items="partenaireItems"
        value-key="value"
        multiple
        :search-input="false"
        placeholder="Tous"
        aria-label="Partenaires"
        class="w-full"
      >
        <template v-if="partenaireDisplayLabel" #default>
          <span class="truncate">{{ partenaireDisplayLabel }}</span>
        </template>

        <template #item-leading="{ item }">
          <img
            v-if="item.icon"
            :src="item.icon"
            alt=""
            class="size-5 object-contain shrink-0"
          >
        </template>
      </USelectMenu>
    </UFormField>

    <UFormField label="Support de diffusion">
      <USelectMenu
        v-model="support"
        :items="supportItems"
        multiple
        disabled
        :search-input="false"
        placeholder="Tous"
        aria-label="Support de diffusion"
        class="w-full"
      />
    </UFormField>

    <UFormField label="Statut de diffusion">
      <USelectMenu
        v-model="statut"
        :items="statutItems"
        multiple
        :search-input="false"
        placeholder="Tous"
        aria-label="Statut de diffusion"
        class="w-full"
      />
    </UFormField>
  </SearchSidebar>
</template>
